<script setup>
import ChecktickSVG from '@/assets/images/global/checkbox.svg?component';
import { classList, isMobileView } from '@js/utils/toolbox';

const props = defineProps({
	emptyLabel: String,
	labelKey: {
		type: String,
		default: 'text'
	},
	valueKey: {
		type: String,
		default: 'value'
	},
	childrenKey: {
		type: String,
		default: 'values'
	},
	searchable: {
		type: Boolean,
		default: false
	}
});

const items = defineModel('items', {
	type: Array,
	required: true
});


// Array of selected items
const selected = defineModel('selected', {
	type: Array,
	required: true
});

const search = ref('');

const itemsFiltered = computed(() => {
	if (!props.searchable) {
		return items.value;
	}
	if (!search.value) {
		return items.value;
	}
	const itemsWithMatch = [];
	const searchValue = search.value.toLowerCase();
	for (const item of items.value) {
		// Check if item has a values array (nested structure)
		if (item.values) {
			const matchingChildren = item.values.filter(child => child.text.toLowerCase().includes(searchValue));
			if (matchingChildren.length > 0) {
				// If there are matching children, include the parent item with only those children
				itemsWithMatch.push({
					...item,
					values: matchingChildren
				});
			}
		}

		// Check direct match
		if (item.text.toLowerCase().includes(searchValue)) {
			itemsWithMatch.push(item);
		}
	}

	return itemsWithMatch;
});

// Helper function to get value(s) from an item
function getItemValues (item) {
	const value = item[props.valueKey];
	return Array.isArray(value) ? value : [value];
}

// Helper function to check if two items match (comparing their values)
function itemsMatch (item1, item2) {
	const values1 = getItemValues(item1);
	const values2 = getItemValues(item2);

	// Check if all values from item1 are in item2 and vice versa
	return values1.length === values2.length
		&& values1.every(v => values2.includes(v));
}

function isItemInSelected (item) {
	// Find the original unfiltered item by text label to ensure we check ALL children
	const originalItem = items.value.find(i => i[props.labelKey] === item[props.labelKey]);

	if (!originalItem) {
		// If not found in top level, it might be a child item - just check if it's selected
		return selected.value.some(selectedItem => itemsMatch(selectedItem, item));
	}

	// Get all children from the original (unfiltered) item
	const allItems = getAllItems(originalItem);
	return allItems.every(_item => selected.value.some(selectedItem => itemsMatch(selectedItem, _item)));
}

function toggleItem (item) {
	// Parent item
	if (item.values) {
		const originalItem = items.value.find(i => i[props.labelKey] === item[props.labelKey]);
		const isInSelected = isItemInSelected(originalItem);
		const allItems = getAllItems(originalItem);
		if (isInSelected) {
			// Remove all child items from selected
			removeItemsWithoutDestroyingReference(allItems);
		} else {
			// Filter out duplicates before adding
			const itemsToAdd = allItems.filter(_item => !selected.value.some(selectedItem => itemsMatch(selectedItem, _item)));
			selected.value.push(...itemsToAdd);
		}
		return;
	}

	const isInSelected = isItemInSelected(item);
	if (isInSelected) {
		// Remove item from selected
		const index = selected.value.findIndex(selectedItem => itemsMatch(selectedItem, item));
		if (index !== -1) {
			selected.value.splice(index, 1);
		}
	} else {
		// Add item to selected
		selected.value.push(item);
	}
}


/**
 * If we write selected.value = selected.value.filter() it will break the reactivity in some edge cases, this method is a workaround
 * @param items
 */
function removeItemsWithoutDestroyingReference (items) {
	items.forEach((item) => {
		const index = selected.value.findIndex(selectedItem => itemsMatch(selectedItem, item));
		if (index !== -1) {
			selected.value.splice(index, 1);
		}
	});
}


function getAllItems (item) {
	if (item[props.childrenKey]) {
		const allItems = [];
		item[props.childrenKey].forEach((child) => {
			allItems.push(...getAllItems(child));
		});
		return allItems;
	}
	return [item];
}


// clickoutside
const container = ref(null);
function useClickOutside (element) {
	// element mirror
	const isFocused = ref(false);
	function handleClickOutside (e) {
		// Use closest to check if the clicked element is inside the container
		if (!element.value.contains(e.target)) {
			isFocused.value = false;
		}
	}

	function toggleFocus () {
		isFocused.value = !isFocused.value;
	}
	onUnmounted(() => document.removeEventListener('click', handleClickOutside));
	onMounted(() => document.addEventListener('click', handleClickOutside));

	watch(isFocused, (newVale) => {
		if (!isMobileView()) {
			return;
		}
		classList(document.body).toggle('modal-open', newVale);
		classList('#overlay').toggle('display', newVale);
	});

	return { isFocused, handleClickOutside, toggleFocus };
}

const { toggleFocus, isFocused } = useClickOutside(container);

</script>

<template>
  <div
    ref="container"
    class="baseCombo"
    :class="{ 'baseCombo--focused': isFocused }"
  >
    <!-- Activator button -->
    <slot
      name="activator"
      :is-focused="isFocused"
      :toggle-focus="toggleFocus"
      :toggle-item="toggleItem"
    >
      <div class="abBtn abBtn--whiteBlue w-full" @click="toggleFocus">
        {{ selected.length ? `${selected.length} selected` : emptyLabel }}
      </div>
    </slot>
    <!-- Searchable Input and Select Menu using Vue 3 Composition API and slots -->
    <div
      v-show="isFocused"
      class="baseCombo__items"
      :class="{ 'baseCombo__items--focus': isFocused }"
      @click="isFocused = true"
    >
      <slot
        v-if="searchable"
        name="search"
        :search="search"
      >
        <div class="baseCombo__input">
          <input
            v-model="search"
            type="text"
            class="input"
            :placeholder="$l('global.simplePhrases.search')"
          >
          <span
            class="icon icon-cross baseCombo__input--clear"

            @click="search = ''"
          />
        </div>
      </slot>
      <ul class="baseCombo__list">
        <BaseComboItem
          v-for="(rootItem, index) in itemsFiltered"
          :key="index"
          :items="rootItem"
          :selected="selected"
          :label-key="labelKey"
          :value-key="valueKey"
          :children-key="childrenKey"
        >
          <template #header="{ item }">
            <slot
              name="header"
              :item="item"
              :toggle-item="toggleItem"
              :is-item-in-selected="isItemInSelected"
            >
              <li @click="toggleItem(item)">
                <label class="checkbox" @click.prevent>
                  <input
                    type="checkbox"
                    :checked="isItemInSelected(item)"
                    class="checkbox__input"
                  >
                  <div class="checkbox__tick multi-select__label">
                    <div class="checkbox__tick--svg"><ChecktickSVG /></div>
                    <span>{{ item.text }}</span>
                  </div>
                </label>
              </li>
            </slot>
          </template>
          <template #item="{ item }">
            <slot
              name="item"
              :item="item"
              :toggle-item="toggleItem"
              :is-item-in-selected="isItemInSelected"
            >
              <li class="item" @click="toggleItem(item)">
                <label class="checkbox" @click.prevent>
                  <input
                    type="checkbox"
                    :checked="isItemInSelected(item)"
                    class="checkbox__input"
                  >
                  <div class="checkbox__tick multi-select__label">
                    <div class="checkbox__tick--svg"><ChecktickSVG /></div>
                    <span>{{ item.text }}</span>
                  </div>
                </label>
              </li>
            </slot>
          </template>
        </BaseComboItem>
      </ul>
      <slot
        name="footer"
        :is-focused="isFocused"
        :toggle-focus="toggleFocus"
      >
        <div class="baseCombo__footer">
          <button class="abBtn abBtn--blueWhite w-full" @click.stop.prevent="toggleFocus">
            {{ $l('global.simplePhrases.apply') }}
          </button>
        </div>
      </slot>
    </div>
  </div>
</template>


<style scoped lang="scss">
.baseCombo {
  position: relative;
  width: 100%;
}

.baseCombo--focused {
  z-index: $z-Search;

  // Mobile: turn the whole combo into a centered "sheet"
  @include breakpoint_down(md) {
    position: fixed;
    top: 10%;
    left: 50%;
    transform: translate(-50%, -50%);

    // small space from screen edges
    width: min(36rem, calc(100vw - 2rem));
    max-height: min(80vh, calc(100vh - 2rem));
    // border-radius: $borderRadius-md;
    border-top-left-radius: $borderRadius-sm;
    border-top-right-radius: $borderRadius-sm;
  }
}


.input {
  width: 100%;
  padding: 1rem;
  background: #e8f3ff;
  color: #2e5b8b;
  border-radius: .9rem;
  box-shadow: 0 -.2rem .2em #31527a1a;
}

.baseCombo__input {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 1rem;
  width: 100%;
  background: white;
}

.baseCombo__input--clear {
  position: absolute;
  right: 2.5rem;
  top: 48%;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.6rem;
  color: $color-blue-700;
}

.baseCombo__items {
  overflow: hidden;
  background-color: white;
  border: 1px solid $neutral-border-color;
  box-shadow: $thinBoxShadow;
  border-bottom-right-radius: $borderRadius-sm;
  border-bottom-left-radius: $borderRadius-sm;
  z-index: 1000;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  max-height: calc(var(--window-height) - 256px);
}

.baseCombo__items--focus {
  position: absolute;
  top: calc(100% - .3rem);
  left: 0;
  right: 0;
  z-index: 9999;
  background-color: white;
}

.baseCombo__list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0;
  margin: 0;
  background-color: white;
  list-style: none;
}

.baseCombo__footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background-color: white;
  border-top: 1px solid $neutral-border-color;
  padding: 1rem;
  width: 100%;
}

/* Desktop behavior */
@media (min-width: 768px) {
  .baseCombo__items {
    padding-bottom: 0;
    max-height: 40vh;
  }

  .baseCombo__footer {
    display: none;
  }
}

.header {
  border-bottom: .1rem solid $color-grey-200;
}

.item {
  padding: 0.5rem;
  padding-left: 1rem;
  border-bottom: .1rem solid $color-grey-200;
}

ul ul .item {
  padding-left: 2rem;
}

ul li {
  cursor: pointer;
  padding: 0.5rem;
  padding-left: 1rem;
  border-bottom: .1rem solid $color-grey-200;
}

li:hover {
  background-color: $color-blue-200;
}

.checkbox__tick {
  background-color: unset !important;
  gap: .5rem;
}

</style>
